import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { AuthResponse, ApiError } from '../types/auth';
interface ErrorResponse {
  message: string;
  details?: string;
  code?: string;
}


class ApiService {
  private static instance: ApiService;
  private api: AxiosInstance;

  private constructor() {
    this.api = axios.create({
      baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 30000
    });

    this.setupInterceptors();
  }

  static getInstance(): ApiService {
    if (!ApiService.instance) {
      ApiService.instance = new ApiService();
    }
    return ApiService.instance;
  }

  getClient(): AxiosInstance {
    return this.api;
  }

  

  private handleRequest(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
  
    if (process.env.NODE_ENV === 'development') {
      console.log('API Request:', {
        method: config.method,
        url: config.url,
        data: config.data
      });
    }
  
    return config;
  }

  private setupInterceptors(): void {
    this.api.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => this.handleRequest(config),
      this.handleRequestError.bind(this)
    );
    this.api.interceptors.request.use(config => {
      const token = localStorage.getItem('auth_token');
      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
    
    // Response interceptor
    this.api.interceptors.response.use(
      response => response,
      (error: AxiosError) => {
        // Handle token expiration
        if (error.response?.status === 401) {
          localStorage.removeItem('auth_token');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }
  

  private handleRequestError(error: AxiosError): Promise<never> {
    return Promise.reject(this.normalizeError(error));
  }

  private handleResponse(response: AxiosResponse): AxiosResponse {
    // Log response in development
    if (process.env.NODE_ENV === 'development') {
      console.log('API Response:', {
        status: response.status,
        data: response.data
      });
    }

    return response;
  }

  private handleResponseError(error: AxiosError): Promise<never> {
    const normalizedError = this.normalizeError(error);

    // Handle authentication errors
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }

    // Handle rate limiting
    if (error.response?.status === 429) {
      // Implement retry logic or user notification
    }

    return Promise.reject(normalizedError);
  }

  private normalizeError(error: AxiosError): Error {
    if (error.response?.data) {
      const errorData = error.response.data as ErrorResponse;
      const message = errorData.message || 'An unknown error occurred';
      const details = errorData.details ? `: ${errorData.details}` : '';
      return new Error(`${message}${details}`);
    }

    if (error.message === 'Network Error') {
      return new Error('Unable to connect to the server. Please check your internet connection.');
    }

    return error;
  }

  // Utility methods for common operations
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.get<T>(url, config);
    return response.data;
  }

  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.post<T>(url, data, config);
    return response.data;
  }

  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.put<T>(url, data, config);
    return response.data;
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.delete<T>(url, config);
    return response.data;
  }
}

export const apiService = ApiService.getInstance();
export default apiService.getClient();